import React from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Typography from '@mui/material/Typography';
import { isEmpty } from 'lodash';

import './PatientsCountHelperText.scss';

const APPROACHING_THRESHOLD_RATIO = 0.9;
const COUNT_ERROR_MESSAGE = "We couldn't calculate your results. Please try adjusting your filters.";

export default function PatientsCountHelperText({
  patientsFilterCountModel,
  patientDatabaseThresholdCount,
  filtersState,
  hasError
}) {
  const hasActiveFilters =
    !isEmpty(filtersState?.ssu?.selectedStudies) ||
    !isEmpty(filtersState?.ssu?.selectedSites) ||
    !isEmpty(filtersState?.indication.value) ||
    !isEmpty(filtersState?.therapeuticArea.value);

  if (!hasActiveFilters) {
    return null;
  }

  if (hasError) {
    return (
      <div className="patient-database-filters-helper-text">
        <ErrorOutlineIcon
          className="patient-database-filters-helper-text-icon"
          data-testid="patients-count-helper-text-icon"
          sx={{ fontSize: 18, color: 'error.main', flexShrink: 0 }}
        />
        <Typography variant="body2" color="error.main">
          {COUNT_ERROR_MESSAGE}
        </Typography>
      </div>
    );
  }

  if (!patientsFilterCountModel) {
    return null;
  }

  const { allPatientsCount, filterPatientsCount } = patientsFilterCountModel;
  const { message, messageColor, Icon } = resolveMessageState(filterPatientsCount, patientDatabaseThresholdCount);

  return (
    <div className="patient-database-filters-helper-text">
      {Icon && (
        <Icon
          className="patient-database-filters-helper-text-icon"
          data-testid="patients-count-helper-text-icon"
          sx={{ fontSize: 18, color: messageColor, flexShrink: 0 }}
        />
      )}
      <Typography variant="body2">
        <Typography component="span" variant="body2" color="text.secondary">
          {`Your filters will return ${formatCount(filterPatientsCount)} of ${formatCount(allPatientsCount)} rows.`}
        </Typography>{' '}
        <Typography component="span" variant="body2" color={messageColor}>
          {message}
        </Typography>
      </Typography>
    </div>
  );
}

function resolveMessageState(filterPatientsCount, patientDatabaseThresholdCount) {
  const thresholdNumber = Number(patientDatabaseThresholdCount);
  const countNumber = Number(filterPatientsCount);

  if (countNumber > thresholdNumber) {
    return {
      message: "That's too many to display at once; try narrowing your filters.",
      messageColor: 'error.main',
      Icon: ErrorOutlineIcon
    };
  }

  if (countNumber >= thresholdNumber * APPROACHING_THRESHOLD_RATIO) {
    return {
      message: "You're approaching the limit; consider narrowing your filters.",
      messageColor: 'warning.main',
      Icon: WarningAmberIcon
    };
  }

  return { message: "You're good to go!", messageColor: 'success.main', Icon: CheckCircleOutlineIcon };
}

function formatCount(count) {
  return Number(count).toLocaleString('en-US');
}
